// src/encounter/drug-interaction-warning.tsx
// Inline drug-interaction warning shown at medication entry (PROD parity — Flutter
// `drug_interaction_info_widget.dart` DrugInteractionInfoWidget + DrugInteractionRowWidget, rendered
// under the med search in `new_medication_add_update_widget.dart`).
//
// Presentational only (no hooks / no network) — fed the `PatientDrugInteraction[]` the host's
// `useDrugInteractions` returns, exactly like the other dialog bodies are fed their search results.
// Three states, matching Flutter's `searchBar()`:
//   - loading            → nothing (Flutter renders an empty Container while the check is in flight)
//   - interactions found → the alert list, most-severe first (DrugInteractionInfoWidget)
//   - none (settled)     → nothing. NOT Flutter's green "No drug-drug interaction" confirmation
//                          (noAllergyWidget): PROD-2721 removed it. An empty answer does not prove a
//                          check ran (a 404 is mapped to [] in useDrugInteractions, and the licensed
//                          engine has expired), so an all-clear was misleading. The R1 limit is said
//                          instead by NoMedicationChecksNotice.
//
// Colour is DS status tokens only (DS-COLOR-001: status triad on the value); the severity badge uses
// the DS StatusChip `soft` tone (the clinical-severity variant), the same mapping as the D/I chart tab.
// The warning/error panels compose the DS `Banner` (PROD-1034) — not hand-rolled card divs — same
// primitive `ClinicalEditRail` uses for its severity → variant panels.
import { useState } from 'react';
import { Banner, Button, StatusChip } from '@scripsteam/scrips-signal-ds';
import type { PatientDrugInteraction } from '@/types/patient-chart-api';

export interface DrugInteractionWarningProps {
  /** Alerts returned by the interaction engine for the med being entered. */
  interactions: PatientDrugInteraction[] | undefined;
  /** True while the check is in flight — renders nothing (Flutter's empty Container). */
  loading?: boolean;
  /**
   * The interaction check FAILED (400 / 500 / network). The safety check could not run, so we must
   * NOT pass silently — surface an explicit failure + retry instead
   * (Flutter's `ShowErrorState`). Never let a failed check silently pass as a clean result.
   */
  error?: boolean;
  /** Re-run the interaction check after a failure. */
  onRetry?: () => void;
  /**
   * When set, show only the alerts triggered BY this medication code (Flutter filters the response to
   * `element.id == item.description`, i.e. the code of the drug just picked). Omit to show all.
   */
  triggerCode?: string | null;
}

const WRAP = 'flex flex-col gap-2';
const META_ROW =
  'flex flex-wrap items-center gap-2 mt-1 text-size-xs text-text-secondary';
const SECTION_LABEL =
  'mt-2 mb-1 text-size-xs font-bold tracking-label uppercase text-text-muted';
const DESC = 'm-0 text-size-sm text-text-secondary whitespace-pre-line';
const MORE_BTN =
  'border-0 bg-transparent p-0 cursor-pointer text-size-xs font-semibold text-status-info';

/** Flutter `HtmlHelper.parseHtmlString`: <br> → newline, strip the remaining tags for plain text. */
function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .trim();
}

/** severe/high → error, moderate → warning, mild/low → info (D/I chart-tab mapping). */
function severitySemantic(
  severity: string | null | undefined,
): 'error' | 'warning' | 'info' | 'neutral' {
  const s = (severity ?? '').toLowerCase();
  if (s.includes('severe') || s.includes('high')) return 'error';
  if (s.includes('moderate')) return 'warning';
  if (s.includes('mild') || s.includes('low')) return 'info';
  return 'neutral';
}

/**
 * Same mapping as `severitySemantic`, collapsed to the DS `Banner`'s variant set (no "neutral").
 * An unrecognised/blank severity defaults to "warning" — never downgrade an unclear clinical
 * severity to a less-alarming panel (matches `clinicalEditVariant`'s safety-first default).
 */
function bannerVariant(severity: string | null | undefined): 'error' | 'warning' | 'info' {
  const s = (severity ?? '').toLowerCase();
  if (s.includes('severe') || s.includes('high')) return 'error';
  if (s.includes('mild') || s.includes('low')) return 'info';
  return 'warning';
}

function severityRank(severity: string | null | undefined): number {
  const s = (severity ?? '').toLowerCase();
  if (s.includes('severe') || s.includes('high')) return 3;
  if (s.includes('moderate')) return 2;
  if (s.includes('mild') || s.includes('low')) return 1;
  return 0;
}

export function DrugInteractionWarning({
  interactions,
  loading,
  error,
  onRetry,
  triggerCode,
}: DrugInteractionWarningProps) {
  // Flutter renders an empty Container while LoadingBeginState is active (no flicker of the OK row).
  if (loading) return null;
  // The check failed — do NOT fall through to an empty, silent result. Surface the
  // failure + a retry so the clinician knows the safety check did not run (Flutter ShowErrorState).
  if (error) {
    return (
      <Banner
        variant="error"
        role="alert"
        action={onRetry ? <Button variant="tertiary" size="sm" onClick={onRetry}>Retry</Button> : undefined}
      >
        Couldn’t check drug interactions. This safety check did not run.
      </Banner>
    );
  }
  if (!interactions) return null;

  const rows = interactions
    .filter((r) => r.isDeleted !== true)
    .filter((r) => (triggerCode ? r.id === triggerCode : true));

  // Settled with no alerts → nothing (see the header: an empty answer is not an all-clear).
  if (rows.length === 0) return null;

  const sorted = [...rows].sort(
    (a, b) => severityRank(b.severity) - severityRank(a.severity),
  );

  return (
    <div className={WRAP} role="alert" aria-label="Drug interaction warning">
      {sorted.map((row, i) => (
        <InteractionRow key={row.id ?? `${row.triggeredBy}-${row.title}-${i}`} row={row} />
      ))}
    </div>
  );
}

function InteractionRow({ row }: { row: PatientDrugInteraction }) {
  const [expanded, setExpanded] = useState(false);
  const description = row.description ? stripHtml(row.description) : '';
  return (
    <Banner
      variant={bannerVariant(row.severity)}
      title={row.title?.trim() || row.medicationname?.trim() || 'Drug interaction'}
    >
      <div className={META_ROW}>
        {row.severity?.trim() ? (
          <StatusChip tone="soft" semantic={severitySemantic(row.severity)}>
            {row.severity}
          </StatusChip>
        ) : null}
        {row.subType?.trim() ? (
          <span>
            Severity: <strong className="text-text-primary">{row.subType}</strong>
          </span>
        ) : null}
        {row.triggeredBy?.trim() ? <span>Triggered by: {row.triggeredBy}</span> : null}
        {description ? (
          <button type="button" className={MORE_BTN} onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Less' : 'More'}
          </button>
        ) : null}
      </div>
      {expanded && description ? (
        <>
          <p className={SECTION_LABEL}>Risks and mechanisms</p>
          <p className={DESC}>{description}</p>
        </>
      ) : null}
    </Banner>
  );
}
